/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/TestSettingsView/TestSettingsView" />
import * as React from "react";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue, ObservableValue, useObservable } from "VSS/Core/Observable";
import { IVssLayoutManager, usePageContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Header, TitleSize } from "VSSUI/Header";
import { Icon, IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextField } from "VSSUI/TextField";
import { BoardTestSettings } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { SelectTestPlanPanel } from "Wit/Common/KanbanBoardSettings/Components/TestSettingsView/SelectTestPlanPanel";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";

export function getTestSettingsTab(
    testSettingsData: ITestSettingsData,
    originalTestSettings: BoardTestSettings | undefined,
    disabled: boolean,
    groupId?: string,
    onSaveSettings?: () => Promise<void>
): ISettingsView {
    const isDirty = new ObservableValue(false);
    const isValid = new ObservableValue(true);
    return {
        groupId,
        isDirty: isDirty,
        isValid: isValid,
        name: Resources.TestSettingsTab,
        onSaveSettings,
        renderContent: () => (
            <TestSettingsView disabled={disabled} isDirty={isDirty} originalTestSettings={originalTestSettings} testSettingsData={testSettingsData} />
        )
    };
}

export function getTestSettingsData(testSettings?: BoardTestSettings): ITestSettingsData {
    return {
        sameTestOutcomes: new ObservableValue(!!testSettings?.sameTestOutcomes),
        testCreationOption: new ObservableValue(testSettings?.testPlanId ? TestCreationOptions.SelectPlan : TestCreationOptions.CreateNewPlan),
        testPlanId: testSettings?.testPlanId ? Number(testSettings?.testPlanId) : undefined
    };
}

export interface ITestSettingsData {
    sameTestOutcomes: IObservableValue<boolean>;
    testCreationOption: IObservableValue<string>;
    testPlanId?: number;
}

export enum TestCreationOptions {
    CreateNewPlan = "createNewPlan",
    SelectPlan = "selectPlan"
}

interface ITestSettingsViewProps {
    disabled: boolean;
    isDirty: IObservableValue<boolean>;
    originalTestSettings: BoardTestSettings | undefined;
    testSettingsData: ITestSettingsData;
}

/**
 * Customizable field settings view that allows for a list of core fields than can be selected,
 * then configurable additional fields that can be chosen from a supplied list.
 */
const TestSettingsView: React.FunctionComponent<ITestSettingsViewProps> = ({ disabled, isDirty, originalTestSettings, testSettingsData }) => {
    const pageContext = usePageContext();
    const projectId = getTFSProject(pageContext)?.id ?? "";
    const { sameTestOutcomes, testCreationOption, testPlanId } = testSettingsData;
    const [selectedTestPlanTitle] = useObservable<string | undefined>(undefined);

    const updateIsDirty = () => {
        const originalSelection = originalTestSettings?.testPlanId ? TestCreationOptions.SelectPlan : TestCreationOptions.CreateNewPlan;
        let updatedIsDirty = false;
        if (testSettingsData.testCreationOption.value === TestCreationOptions.CreateNewPlan) {
            updatedIsDirty = testSettingsData.testCreationOption.value !== originalSelection;
        } else {
            updatedIsDirty = (testSettingsData.testPlanId?.toString() ?? "") !== originalTestSettings?.testPlanId;
        }

        updatedIsDirty = updatedIsDirty || testSettingsData.sameTestOutcomes.value !== originalTestSettings?.sameTestOutcomes;

        if (updatedIsDirty !== isDirty.value) {
            isDirty.value = updatedIsDirty;
        }
    };

    React.useEffect(() => {
        const loadTestPlanTitle = async () => {
            if (!testPlanId) {
                return;
            }

            const service = pageContext.getService<IWorkItemService>("work-item-service");
            const testPlan = await service.loadWorkItem(projectId, testPlanId, [CoreFieldRefNames.Title]);
            selectedTestPlanTitle.value = testPlan?.fields[CoreFieldRefNames.Title] ?? String(testPlanId);
        };

        loadTestPlanTitle();
    }, []);

    return (
        <div className="board-test-settings flex-column rhythm-vertical-24">
            <Header
                className="padding-vertical-0"
                description={Resources.TestSettingsDescription}
                title={Resources.TestSettingsTab}
                titleSize={TitleSize.Small}
            />
            <div className="flex-column rhythm-vertical-24 padding-horizontal-20">
                <div className="flex-column rhythm-vertical-8">
                    <span className="font-weight-semibold">{Resources.ManualTestPlansSubHeading}</span>
                    <span className="secondary-text">{Resources.ManualTestPlansDescription}</span>
                    <RadioButtonGroup
                        className="flex-noshrink"
                        onSelect={buttonId => {
                            testCreationOption.value = buttonId;
                            updateIsDirty();
                        }}
                        selectedButtonId={testCreationOption}
                    >
                        <RadioButton disabled={disabled} id={TestCreationOptions.CreateNewPlan} text={Resources.ManualTestCreateNewPlan} />
                        <RadioButton disabled={disabled} id={TestCreationOptions.SelectPlan} text={Resources.ManualTestsSelectPlan} />
                    </RadioButtonGroup>
                    <Observer selectedRadioButton={testCreationOption}>
                        {() => {
                            const selectPlanDisabled = disabled || testCreationOption.value !== TestCreationOptions.SelectPlan;
                            return (
                                <span className="flex-row rhythm-horizontal-4">
                                    <TextField
                                        ariaLabel={Resources.SelectedTestPlan}
                                        disabled={selectPlanDisabled}
                                        readOnly
                                        value={selectedTestPlanTitle}
                                    />
                                    <Button
                                        ariaLabel={Resources.SelectTestPlan}
                                        className="choose-test-plan-button"
                                        disabled={selectPlanDisabled}
                                        iconProps={{ iconName: "More" }}
                                        onClick={() => {
                                            const layoutService = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                                            layoutService.renderCallout(onDismiss => (
                                                <SelectTestPlanPanel
                                                    onDismiss={onDismiss}
                                                    onTestPlanSelected={(testPlan: WorkItem) => {
                                                        selectedTestPlanTitle.value = testPlan.fields[CoreFieldRefNames.Title];
                                                        testSettingsData.testPlanId = testPlan.fields[CoreFieldRefNames.Id];
                                                        updateIsDirty();
                                                    }}
                                                />
                                            ));
                                        }}
                                        tooltipProps={{ text: Resources.SelectTestPlan }}
                                    />
                                </span>
                            );
                        }}
                    </Observer>
                </div>
                <div className="flex-column rhythm-vertical-8">
                    <span className="font-weight-semibold">{Resources.ManualTestOutcomesSubHeading}</span>
                    <span className="secondary-text">{Resources.ManualTestOutcomesDescription}</span>
                    <Checkbox
                        checked={sameTestOutcomes}
                        disabled={disabled}
                        label={
                            <span className="flex-row">
                                {Resources.ManualTestOutcomesSameOutcome}
                                <Icon
                                    className="margin-left-4"
                                    iconName="Info"
                                    size={IconSize.small}
                                    tooltipProps={{ text: Resources.ManualTestOutcomesSameOutcomeTooltip }}
                                />
                            </span>
                        }
                        onChange={(event, checked) => {
                            sameTestOutcomes.value = checked;
                            updateIsDirty();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
